import { Box, Input, Slider, Stack, Typography } from '@mui/joy';
import { useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';

// Dragging the slider fires a change per frame, and each one costs the caller a
// re-filter of the whole list. The thumb follows the drag from local state so
// it stays smooth, and the caller only hears about it once the drag pauses --
// or immediately when it ends.
const COMMIT_DELAY_MS = 150;

export function RangeSlider({ label, unit, value, bounds, onChange }) {
  const [minBound, maxBound] = bounds;
  const isDisabled = minBound === maxBound;
  const current = value ?? bounds;
  const [currentMin, currentMax] = current;

  const [minInput, setMinInput] = useState(String(currentMin));
  const [maxInput, setMaxInput] = useState(String(currentMax));
  // What the drag is showing, until the caller's value catches up with it.
  const [dragged, setDragged] = useState(null);

  // The caller passes a fresh onChange every render, so the debounced function
  // reads it from a ref -- rebuilding it each render would restart the timer
  // on every frame and it would never fire.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  const commit = useMemo(
    () => debounce((next) => onChangeRef.current(next), COMMIT_DELAY_MS),
    []
  );
  useEffect(() => () => commit.cancel(), [commit]);

  useEffect(() => {
    setMinInput(String(currentMin));
    setMaxInput(String(currentMax));
    setDragged(null);
  }, [currentMin, currentMax]);

  const handleSliderChange = (event, next) => {
    setDragged(next);
    setMinInput(String(next[0]));
    setMaxInput(String(next[1]));
    commit(next);
  };

  // Typing an end in reads against whatever the slider is showing, which is the
  // dragged value while a commit is still pending. That pending commit is
  // dropped rather than flushed, because this range already includes it.
  const shown = dragged ?? current;

  const commitMin = () => {
    const parsed = Number(minInput);
    const clamped = Number.isNaN(parsed) ? shown[0] : Math.min(Math.max(parsed, minBound), shown[1]);
    commit.cancel();
    onChange([clamped, shown[1]]);
    setMinInput(String(clamped));
  };

  const commitMax = () => {
    const parsed = Number(maxInput);
    const clamped = Number.isNaN(parsed) ? shown[1] : Math.max(Math.min(parsed, maxBound), shown[0]);
    commit.cancel();
    onChange([shown[0], clamped]);
    setMaxInput(String(clamped));
  };

  return (
    <Box>
      <Typography level="title-xs">
        {label}
      </Typography>
      <Box sx={{ px: 1 }}>
        <Slider
          value={shown}
          onChange={handleSliderChange}
          // Letting go should not wait out the debounce.
          onChangeCommitted={() => commit.flush()}
          min={minBound}
          max={Math.max(maxBound, minBound + 1)}
          step={1}
          disabled={isDisabled}
          valueLabelDisplay="off"
          sx={{
            "--Slider-trackSize": "4px",
            mb: -1,
          }}
        />
      </Box>
      <Stack direction="row" gap={1} alignItems="center">
        <Input size="sm" value={minInput} onChange={(e) => setMinInput(e.target.value)} onBlur={commitMin} disabled={isDisabled} sx={{ width: 64, '& input': { textAlign: 'center' } }} />
        <Typography level="body-xs">to</Typography>
        <Input size="sm"value={maxInput} onChange={(e) => setMaxInput(e.target.value)} onBlur={commitMax} disabled={isDisabled} sx={{ width: 64, '& input': { textAlign: 'center' } }} />
        <Typography level="title-xs">{unit}</Typography>
      </Stack>
    </Box>
  );
}
